import '../../../../assets/css/screens/admin/dashboard/adminkyc/AdminKyc.css'

import React, { useState, useEffect, useContext } from 'react';
import {
  FileText, CheckCircle, Clock, XCircle,
  Eye, Search, ChevronDown, X, AlertTriangle,
  ShieldCheck, User, Calendar, Filter
} from 'lucide-react';
import AppContext from '../../../../context/AppContext';
import axios from 'axios';


/* ── Status config ── */
const STATUS_CFG = {
  pending:  { label: 'Pending',  color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  icon: <Clock size={13}/>       },
  approved: { label: 'Approved', color: '#10b981', bg: 'rgba(16,185,129,0.10)', icon: <CheckCircle size={13}/> },
  rejected: { label: 'Rejected', color: '#ef4444', bg: 'rgba(239,68,68,0.10)',  icon: <XCircle size={13}/>     },
};



const AdminKYC = () => {
  const { baseUrl, baseUrlForFile } = useContext(AppContext);

  const [submissions,  setSubmissions]  = useState([]);
const [fetching, setFetching] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery,  setSearchQuery]  = useState('');

  /* detail modal */
  const [selected,    setSelected]    = useState(null); // submission object
  const [rejectInput, setRejectInput] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'approve' | 'reject'

  /* ── Fetch ── */
useEffect(() => {
  const authToken = localStorage.getItem('authToken');

  axios.get(`${baseUrl}/admin/viewkyc`, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  })
  .then(res => {
    
    const data = res.data.kycs || [];

    const formatted = data.map(k => ({
      id: k.id,
      worker_name: k.whserviceprovider?.fullName || "Unknown",
      worker_email: k.whserviceprovider?.email || "No email",
      submitted_at: k.created_at,
      status: k.status,
      pdf_url: k.document
        ? `${baseUrlForFile}/${k.document.replace(/^\/+/, '')}`
        : null,
      note: k.note || ""
    }));

    setSubmissions(formatted);
  })
  .catch(err => console.log(err));

}, []); 

  /* ── Counts ── */
  const counts = {
    all:      submissions.length,
    pending:  submissions.filter(s => s.status === 'pending').length,
    approved: submissions.filter(s => s.status === 'approved').length,
    rejected: submissions.filter(s => s.status === 'rejected').length,
  };

  /* ── Filtered list ── */
  const filtered = submissions.filter(s => {
    const matchStatus = filterStatus === 'all' || s.status === filterStatus;
    const q = searchQuery.toLowerCase();
    const matchSearch = !q ||
      s.worker_name.toLowerCase().includes(q) ||
      s.worker_email.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });
// update status
const updateKycStatus = async (status, reason = "") => {
  const authToken = localStorage.getItem('authToken');

  try {
    await axios.post(
      `${baseUrl}/admin/updateKycStatus/${selected.id}`,
      {
        status: status,          // approved / rejected
        reject_reason: reason    // only for reject
      },
      {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      }
    );

    // update UI instantly (no refresh needed)
    setSubmissions(prev =>
      prev.map(s =>
        s.id === selected.id
          ? { ...s, status: status, reject_reason: reason }
          : s
      )
    );

    setSelected(prev => ({
      ...prev,
      status: status,
      reject_reason: reason
    }));

    setConfirmAction(null);
    setRejectInput("");

  } catch (err) {
    console.log(err.response?.data || err.message);
  }
};

  const openDetail = (sub) => {
    setSelected(sub);
    setConfirmAction(null);
    setRejectInput('');
  };

  const closeDetail = () => {
    setSelected(null);
    setConfirmAction(null);
    setRejectInput('');
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' · ' + d.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });
  };


  return (
    <div className="akyc-page">

      {/* ── Header ── */}
      <div className="akyc-header">
        <div className="akyc-header__left">
          <div className="akyc-header__icon"><ShieldCheck size={22}/></div>
          <div>
            <h2 className="akyc-title">KYC Submissions</h2>
            <p className="akyc-subtitle">Review and verify worker documents</p>
          </div>
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="akyc-stats">
        {[
          { key: 'all',      label: 'Total',    color: '#0491e1' },
          { key: 'pending',  label: 'Pending',  color: '#f59e0b' },
          { key: 'approved', label: 'Approved', color: '#10b981' },
          { key: 'rejected', label: 'Rejected', color: '#ef4444' },
        ].map(({ key, label, color }) => (
          <button
            key={key}
            className={`akyc-stat-card ${filterStatus === key ? 'akyc-stat-card--active' : ''}`}
            style={{ '--sc': color }}
            onClick={() => setFilterStatus(key)}
          >
            <span className="akyc-stat-num" style={{ color }}>{counts[key]}</span>
            <span className="akyc-stat-label">{label}</span>
          </button>
        ))}
      </div>

      {/* ── Search + filter bar ── */}
      <div className="akyc-toolbar">
        <div className="akyc-search">
          <Search size={15}/>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="akyc-search__clear" onClick={() => setSearchQuery('')}>
              <X size={13}/>
            </button>
          )}
        </div>

        <div className="akyc-filter">
          <Filter size={14}/>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <ChevronDown size={13}/>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="akyc-table-wrap">
        {filtered.length === 0 ? (
          <div className="akyc-empty">
            <FileText size={32}/>
            <p>No submissions found</p>
          </div>
        ) : (
          <table className="akyc-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Worker</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Note</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((sub, idx) => {
                const st = STATUS_CFG[sub.status] || STATUS_CFG.pending;
                return (
                  <tr key={sub.id} className="akyc-row">
                    <td className="akyc-td-num">{idx + 1}</td>
                    <td>
                      <div className="akyc-worker-cell">
                        <div className="akyc-avatar">
                          {sub.worker_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <span className="akyc-worker-name">{sub.worker_name}</span>
                          <span className="akyc-worker-email">{sub.worker_email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="akyc-td-date">
                      <Calendar size={12}/> {fmtDate(sub.submitted_at)}
                    </td>
                    <td>
                      <span className="akyc-badge" style={{ color: st.color, background: st.bg }}>
                        {st.icon} {st.label}
                      </span>
                    </td>
                    <td className="akyc-td-note">
                      {sub.note ? <span className="akyc-note-text">{sub.note}</span> : <span className="akyc-note-empty">—</span>}
                    </td>
                    <td>
                      <button className="akyc-review-btn" onClick={() => openDetail(sub)}>
                        <Eye size={13}/> Review
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ══════════════════════════════════════════
          DETAIL MODAL
      ══════════════════════════════════════════ */}
      {selected && (
        <div className="akyc-overlay" onClick={closeDetail}>
          <div className="akyc-modal" onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div className="akyc-modal__header">
              <div className="akyc-modal__title-row">
                <div className="akyc-modal__avatar">
                  {selected.worker_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="akyc-modal__name">{selected.worker_name}</h3>
                  <span className="akyc-modal__email">{selected.worker_email}</span>
                </div>
              </div>
              <button className="akyc-modal__close" onClick={closeDetail}><X size={18}/></button>
            </div>

            {/* Status + date */}
            <div className="akyc-modal__meta">
              {(() => {
                const st = STATUS_CFG[selected.status] || STATUS_CFG.pending;
                return (
                  <span className="akyc-badge akyc-badge--lg" style={{ color: st.color, background: st.bg }}>
                    {st.icon} {st.label}
                  </span>
                );
              })()}
              <span className="akyc-modal__date">
                <Calendar size={13}/> {fmtDate(selected.submitted_at)}
              </span>
            </div>

            {/* Worker note */}
            {selected.note && (
              <div className="akyc-modal__note-box">
                <span className="akyc-modal__note-label">Worker Note</span>
                <p className="akyc-modal__note-text">{selected.note}</p>
              </div>
            )}

            {/* PDF viewer */}
            <div className="akyc-modal__pdf-section">
              <span className="akyc-modal__pdf-label"><FileText size={14}/> KYC Document</span>

              {selected.pdf_url ? (
<div className="akyc-pdf-clickable">
  <FileText size={28} />

  <p>KYC Document</p>

  <button
    className="akyc-open-pdf-btn"
    onClick={() => window.open(selected.pdf_url, '_blank', 'noopener,noreferrer')}
  >
    Open PDF in New Tab
  </button>
</div>
              ) : (
                <div className="akyc-pdf-placeholder">
                  <FileText size={28}/>
                  <span>PDF preview loads here</span>
                  <span className="akyc-pdf-placeholder__hint">
                    Connect real API to serve PDF URL
                  </span>
                </div>
              )}
            </div>

            {/* ── Action area (only for pending) ── */}
            {selected.status === 'pending' && !confirmAction && (
              <div className="akyc-modal__actions">
                <button
                  className="akyc-approve-btn"
                  onClick={() => setConfirmAction('approve')}
                >
                  <CheckCircle size={15}/> Approve
                </button>
                <button
                  className="akyc-reject-btn"
                  onClick={() => setConfirmAction('reject')}
                >
                  <XCircle size={15}/> Reject
                </button>
              </div>
            )}

            {/* Confirm approve */}
            {confirmAction === 'approve' && (
              <div className="akyc-confirm-box akyc-confirm-box--approve">
                <p><CheckCircle size={15}/> Confirm approval for <strong>{selected.worker_name}</strong>?</p>
                <div className="akyc-confirm-btns">
                  <button
                    className="akyc-confirm-yes akyc-confirm-yes--green"
                    onClick={() => updateKycStatus('approved')}
                    disabled={actionLoading}
                  >
                    {actionLoading ? <div className="akyc-btn-spin"/> : null} Yes, Approve
                  </button>
                  <button className="akyc-confirm-no" onClick={() => setConfirmAction(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Confirm reject */}
            {confirmAction === 'reject' && (
              <div className="akyc-confirm-box akyc-confirm-box--reject">
                <p><AlertTriangle size={15}/> Rejection reason <span className="akyc-required">*</span></p>
                <textarea
                  className="akyc-reject-textarea"
                  placeholder="e.g. CNIC not visible, documents incomplete..."
                  value={rejectInput}
                  onChange={e => setRejectInput(e.target.value)}
                  rows={3}
                />
                <div className="akyc-confirm-btns">
                  <button
                    className="akyc-confirm-yes akyc-confirm-yes--red"
                    onClick={() => updateKycStatus('rejected', rejectInput)}
                    disabled={!rejectInput.trim() || actionLoading}
                  >
                    {actionLoading ? <div className="akyc-btn-spin"/> : null} Confirm Reject
                  </button>
                  <button className="akyc-confirm-no" onClick={() => setConfirmAction(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Already actioned message */}
            {selected.status === 'approved' && (
              <div className="akyc-actioned-msg akyc-actioned-msg--green">
                <CheckCircle size={16}/> This submission has been approved.
              </div>
            )}
            {selected.status === 'rejected' && (
              <div className="akyc-actioned-msg akyc-actioned-msg--red">
                <XCircle size={16}/> This submission has been rejected.
              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );
};

export default AdminKYC;